// ==========================================
// Buddy chat — a view onto the active thread
// ==========================================
// Chat drives BB: sending puts him in `thinking`, the first token moves him to
// `speaking` (TTS on) or `idle`, and Esc stops generation (3d INTERACTION FLOW).
//
// Messages live on the thread, not here — BuddyThreadsService owns them. Every write
// takes an optional threadId that defaults to the active thread, so a stream or a
// workflow that started in one conversation keeps landing there after you switch
// away, exactly as ChatGPT does.
//
// The stream is simulated. API-SWAP markers below show where the real endpoint slots
// in; the message shape and streaming contract stay the same, so no component changes.
// ==========================================

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { BuddyMessage, RewardChip, SuggestedReply } from '../models/buddy.types';
import { MILESTONE_COLLAPSE_MS } from '../models/buddy.constants';
import { MOCK_SUGGESTIONS } from './buddy-mock.data';
import { BbStateService } from './bb-state.service';
import { BuddyApiService } from './buddy-api.service';
import { BuddyStateService } from './buddy-state.service';
import { BuddyProgressService } from './buddy-progress.service';
import { BuddyThreadsService } from './buddy-threads.service';

let idCounter = 0;
function nextId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

function emptyMessage(role: BuddyMessage['role'], text: string): BuddyMessage {
  return {
    id: nextId(role),
    role,
    text,
    createdAt: Date.now(),
    streaming: false,
    runId: null,
    reward: null,
    actions: [],
    variant: 'default',
    collapsed: false,
  };
}

@Injectable({ providedIn: 'root' })
export class BuddyChatService implements OnDestroy {
  private readonly streamingSubject = new BehaviorSubject<boolean>(false);

  private streamTimer: ReturnType<typeof setTimeout> | null = null;
  private collapseTimers: ReturnType<typeof setTimeout>[] = [];

  /** The active thread's messages. Switching threads swaps this wholesale. */
  readonly messages: Observable<BuddyMessage[]>;
  readonly isStreaming: Observable<boolean> = this.streamingSubject.pipe(distinctUntilChanged());

  constructor(
    private readonly bb: BbStateService,
    private readonly shell: BuddyStateService,
    private readonly progress: BuddyProgressService,
    private readonly threads: BuddyThreadsService,
    private readonly api: BuddyApiService
  ) {
    this.messages = this.threads.activeThread.pipe(map((t) => t?.messages ?? []));
  }

  get current(): BuddyMessage[] {
    return this.threads.active.messages;
  }

  get streaming(): boolean {
    return this.streamingSubject.value;
  }

  // ------------------------------------------
  // Sending
  // ------------------------------------------

  send(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || this.streaming) {
      return;
    }

    // Pin the thread now so the reply lands here even if the user switches away.
    // '' means a new chat — the backend issues the real id (see adoptThreadId).
    const threadId = this.threads.activeThreadId;

    this.bb.wake();
    this.push(emptyMessage('user', trimmed), threadId);

    // BB holds `thinking` while the request is in flight; block re-entry.
    this.bb.think();

    // Push an empty streaming assistant bubble IMMEDIATELY. While it has no text,
    // the chat log shows a "BB is thinking…" typing indicator (bouncing dots), so
    // the multi-second API round-trip never looks frozen. The real answer then
    // streams into this same bubble.
    const placeholder: BuddyMessage = { ...emptyMessage('assistant', ''), streaming: true };
    this.push(placeholder, threadId);
    this.streamingSubject.next(true);

    this.api.chat({ thread_id: threadId || null, prompt: trimmed }).subscribe({
      next: (res) => {
        // A new chat comes back with a server-issued thread id — adopt it so the
        // user message, the placeholder and the reply all land in the same
        // (now-real) thread (adopt preserves the thread's messages).
        let target = threadId;
        if (res.threadId && res.threadId !== threadId) {
          this.threads.adoptThreadId(threadId, res.threadId, res.threadTitle);
          target = res.threadId;
        }
        // Render the backend's HTML reply whole into the placeholder bubble.
        this.renderReply(res.message?.text ?? '', target, placeholder.id);
      },
      error: () => {
        // Turn the "thinking" bubble into an inline error — no dangling bubble.
        this.replace(
          placeholder.id,
          (m) => ({
            ...m,
            streaming: false,
            text: 'Sorry — I could not reach the assistant. Please try again.',
          }),
          threadId
        );
        this.streamingSubject.next(false);
        this.bb.fail();
        this.bb.settle();
      },
    });
  }

  /** Esc stops generation (3d). The partial reply stays in the log. */
  stop(): void {
    if (!this.streaming) {
      return;
    }
    this.clearStreamTimer();
    this.finishStream();
  }

  // ------------------------------------------
  // Appending from other surfaces
  // ------------------------------------------

  /** Creates the assistant message that hosts a workflow's inline step card. */
  attachRun(runId: string, headline: string, threadId?: string): string {
    const message: BuddyMessage = { ...emptyMessage('assistant', headline), runId };
    this.push(message, threadId);
    return message.id;
  }

  /** Lands the ephemeral +XP chip on a result card. */
  setReward(messageId: string, reward: RewardChip, threadId?: string): void {
    this.replace(messageId, (m) => ({ ...m, reward }), threadId);
  }

  setActions(messageId: string, labels: readonly string[], threadId?: string): void {
    const actions: SuggestedReply[] = labels.map((label, i) => ({
      id: nextId('action'),
      label,
      primary: i === 0,
    }));
    this.replace(messageId, (m) => ({ ...m, actions }), threadId);
  }

  /**
   * Inline milestone banner — never a modal. Collapses to a one-line system message
   * after 4s so the log stays readable (2d).
   */
  appendMilestone(text: string, threadId?: string): void {
    const target = threadId ?? this.threads.activeThreadId;
    const message: BuddyMessage = { ...emptyMessage('system', text), variant: 'milestone' };
    this.push(message, target);
    const timer = setTimeout(() => {
      this.replace(message.id, (m) => ({ ...m, collapsed: true }), target);
    }, MILESTONE_COLLAPSE_MS);
    this.collapseTimers.push(timer);
  }

  appendSystem(text: string, threadId?: string): void {
    this.push(emptyMessage('system', text), threadId);
  }

  // ------------------------------------------
  // Rendering the reply
  // ------------------------------------------

  /**
   * Drops the backend's ready-to-render HTML reply into the "thinking" placeholder
   * bubble in ONE shot, then finalizes. The reply is NOT token-streamed: the answer
   * is HTML (tables, lists), and revealing it a slice at a time would render broken
   * partial tags. The typing indicator covers the wait; this swaps in the whole reply.
   */
  private renderReply(html: string, threadId: string, messageId: string): void {
    this.replace(messageId, (m) => ({ ...m, text: html }), threadId);
    this.finishStream(threadId);
  }

  private finishStream(threadId?: string): void {
    this.clearStreamTimer();
    this.streamingSubject.next(false);

    const target = threadId ?? this.threads.activeThreadId;
    const messages = this.threads.byId(target)?.messages ?? [];
    const last = messages[messages.length - 1];
    if (last && last.streaming) {
      this.replace(last.id, (m) => ({ ...m, streaming: false }), target);

      // Offer suggested replies at the comfortable density (3c).
      if (this.shell.currentDensity === 'comfortable') {
        this.setActions(last.id, MOCK_SUGGESTIONS, target);
      }

      // A completed exchange pays XP, subject to the daily soft cap.
      const award = this.progress.awardChatExchange();
      if (award) {
        this.setReward(last.id, award.reward, target);
        if (award.leveledUp) {
          this.bb.celebrate();
          this.appendMilestone(`Level ${award.newLevel} — BB grows into it.`, target);
        }
      }

      // The thread earns its title from the first exchange, as ChatGPT does.
      this.threads.autoTitle(target);
    }

    this.bb.settle();
    this.bb.noteActivity();
  }

  // ------------------------------------------
  // Store helpers — all writes go through the threads service
  // ------------------------------------------

  private push(message: BuddyMessage, threadId?: string): void {
    const target = threadId ?? this.threads.activeThreadId;
    const thread = this.threads.byId(target);
    if (!thread) {
      return;
    }
    this.threads.setMessages(target, [...thread.messages, message]);
  }

  private replace(
    id: string,
    update: (m: BuddyMessage) => BuddyMessage,
    threadId?: string
  ): void {
    const target = threadId ?? this.threads.activeThreadId;
    const thread = this.threads.byId(target);
    if (!thread) {
      return;
    }
    let changed = false;
    const next = thread.messages.map((m) => {
      if (m.id !== id) {
        return m;
      }
      changed = true;
      return update(m);
    });
    if (changed) {
      this.threads.setMessages(target, next);
    }
  }

  private clearStreamTimer(): void {
    if (this.streamTimer !== null) {
      clearTimeout(this.streamTimer);
      this.streamTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearStreamTimer();
    this.collapseTimers.forEach((t) => clearTimeout(t));
    this.collapseTimers = [];
  }
}
